// apps/api/src/qxcircle/qxcircle-media.ts
// Description: Validate local QXCircle image/video attachments (Slice 5).
// Owner: Masa Bayu
// Date: 2026-08-24

import { BadRequestException } from "./_nestjs-common";
import type {
  CreateQxCirclePostMediaInput,
  QxCirclePostMedia,
} from "./qxcircle.types";
import {
  QXCIRCLE_IMAGE_MAX_BYTES,
  QXCIRCLE_IMAGE_TYPES,
  QXCIRCLE_VIDEO_MAX_BYTES,
  QXCIRCLE_VIDEO_TYPES,
} from "./qxcircle.types";

export function normalizeQxCircleMedia(
  input: CreateQxCirclePostMediaInput | null | undefined,
  postId: string,
): QxCirclePostMedia | null {
  if (!input) return null;

  const kind = input.kind;
  const contentType = String(input.contentType ?? "").toLowerCase();
  const fileName = String(input.fileName ?? "").trim() || "attachment";
  const byteSize = Number(input.byteSize ?? 0);
  const previewDataUrl = String(input.previewDataUrl ?? "").trim();

  if (kind !== "image" && kind !== "video") {
    throw new BadRequestException("Media kind must be image or video");
  }
  if (!Number.isFinite(byteSize) || byteSize <= 0) {
    throw new BadRequestException("Media byteSize required");
  }
  if (!previewDataUrl.startsWith("data:")) {
    throw new BadRequestException("Media previewDataUrl must be a data URL");
  }

  if (kind === "image") {
    if (!(QXCIRCLE_IMAGE_TYPES as readonly string[]).includes(contentType)) {
      throw new BadRequestException(
        `Image type must be ${QXCIRCLE_IMAGE_TYPES.join(", ")}`,
      );
    }
    if (byteSize > QXCIRCLE_IMAGE_MAX_BYTES) {
      throw new BadRequestException(
        `Image max ${QXCIRCLE_IMAGE_MAX_BYTES} bytes`,
      );
    }
    if (!previewDataUrl.startsWith("data:image/")) {
      throw new BadRequestException("Image preview must be data:image/…");
    }
  } else {
    if (!(QXCIRCLE_VIDEO_TYPES as readonly string[]).includes(contentType)) {
      throw new BadRequestException(
        `Video type must be ${QXCIRCLE_VIDEO_TYPES.join(", ")}`,
      );
    }
    if (byteSize > QXCIRCLE_VIDEO_MAX_BYTES) {
      throw new BadRequestException(
        `Video max ${QXCIRCLE_VIDEO_MAX_BYTES} bytes`,
      );
    }
    if (!previewDataUrl.startsWith("data:video/")) {
      throw new BadRequestException("Video preview must be data:video/…");
    }
  }

  return {
    kind,
    contentType,
    fileName,
    byteSize,
    objectRef: `media://local/qxcircle/${postId}`,
    previewDataUrl,
  };
}
